import {
  Avatar,
  Box,
  ClickAwayListener,
  Divider as MuiDivider,
  IconButton,
  List,
  ListItem,
  Paper,
  Popover as MuiPopover,
  PopoverOrigin,
  Tooltip,
  Typography,
  Switch,
} from '@mui/material'
import { styled } from '@mui/system'
import {
  BorderColor,
  FontWeight,
  IconColor,
  TextColor,
} from '../../../styles/commonStyles'
import DateVO from '../../../vo/DateVO'
import { OpenInNewRounded } from '@mui/icons-material'
import { WbsItemStatus } from '../../containers/commons/AgGrid/components/cell/custom/wbsItemStatus'
import WbsItemStatusLabel from '../labels/WbsItemStatusLabel'
import { injectIntl, WrappedComponentProps } from 'react-intl'
import AccessAlarmIcon from '@mui/icons-material/AccessAlarm'
import { DISPLAY_DATE_FORMAT_WITH_DAY } from '../../../utils/date'
import { DelayIcon } from '../../containers/commons/AgGrid/components/cell/custom/wbsItemStatus/delayIcon'
import { BaseWbsItemType } from '../../../store/project'
import { WbsItemTypeVO } from '../../../domain/value-object/WbsItemTypeVO'
import { AllState } from '../../../store'
import { connect, useDispatch } from 'react-redux'
import { ProjectDetail } from '../../../lib/functions/project'
import { intl } from '../../../i18n'
import { useMemo, useState, useRef, useEffect, useCallback, memo } from 'react'
import { colorPalette } from '../../style/colorPalette'
import { pushFunctionLayer } from '../../../store/functionLayer'
import { APPLICATION_FUNCTION_EXTERNAL_ID } from '../../pages'
import { convertDecimalHourToHM } from '../../../utils/time'
import { useTimeFormatState } from './hooks/useTimeFormatState'
import { UserEntity } from '../../../domain/entity/UserEntity'
import { StaleIndicatorIcon } from '../icons/StaleIndicatorIcon'

const HEADER_HEIGHT = 32
const FOOTER_HEIGHT = 32
const MIN_PATH_PARTS_FOR_ELLIPSIS = 2
interface StateProps {
  wbsItemTypes: BaseWbsItemType
}
interface Props extends StateProps, WrappedComponentProps {
  anchorEl: any
  date: DateVO
  actualWorks: ActualWork[]
  actualHour: number
  event: PointerEvent
  onClose: () => void
  openDetail?: () => void
}
export type ActualWork = {
  project?: ProjectDetail
  taskCode: string
  taskName: string
  taskStatus: WbsItemStatus
  hour: number
  endDate: string
  ticketType: string
  wbsItemType: WbsItemTypeVO
  path?: string
  pathStale?: boolean
  proxyUser?: UserEntity
}
const PathDisplay = memo(
  ({
    path: originalPath,
    pathStale,
    onScrollChange,
  }: {
    path?: string
    pathStale?: boolean
    onScrollChange?: (hasScroll: boolean) => void
  }) => {
    const [isExpanded, setIsExpanded] = useState(false)
    const path = useMemo(
      () =>
        originalPath || intl.formatMessage({ id: 'wbsItemPath.calculating' }),
      [originalPath]
    )
    const pathRef = useRef<HTMLParagraphElement>(null)
    const onScrollChangeRef = useRef(onScrollChange)

    useEffect(() => {
      onScrollChangeRef.current = onScrollChange
    }, [onScrollChange])

    const parts = useMemo(() => path.split('/'), [path])
    const needsEllipsis = useMemo(
      () => parts.length > MIN_PATH_PARTS_FOR_ELLIPSIS,
      [parts.length]
    )

    const [needsScroll, setNeedsScroll] = useState(false)
    const isClickable = needsEllipsis || needsScroll

    useEffect(() => {
      const element = pathRef.current
      if (!element) return

      const hasOverflow = element.scrollWidth > element.clientWidth
      setNeedsScroll(hasOverflow)

      onScrollChangeRef.current?.(isExpanded && hasOverflow)
    }, [isExpanded, path])

    const staleIcon = pathStale ? (
      <StaleIndicatorIcon
        message={intl.formatMessage({ id: 'viewModel.stale.path' })}
      />
    ) : null

    const isCalculating = !originalPath
    const calculatingSx = isCalculating
      ? { color: colorPalette.monotone[4] }
      : {}

    if (isExpanded) {
      return (
        <PathDisplayRow>
          <TaskPathExpanded ref={pathRef} sx={calculatingSx}>
            {path}
          </TaskPathExpanded>
          {staleIcon}
        </PathDisplayRow>
      )
    }
    const content = (
      <TaskPath
        ref={pathRef}
        onClick={isClickable ? () => setIsExpanded(true) : undefined}
        sx={{
          cursor: isClickable ? 'pointer' : 'default',
          ...calculatingSx,
          '&:hover': isClickable
            ? {
                color: colorPalette.blue[8],
                backgroundColor: colorPalette.blue[0],
              }
            : {},
        }}
      >
        {needsEllipsis ? (
          <>
            {parts[0]}/<TaskPathEllipsis>...</TaskPathEllipsis>/
            {parts[parts.length - 1]}
          </>
        ) : (
          path
        )}
      </TaskPath>
    )
    const wrappedContent = isClickable ? (
      <Tooltip
        title={intl.formatMessage({ id: 'taskActualWork.dialog.path.expand' })}
        placement="top"
      >
        {content}
      </Tooltip>
    ) : (
      content
    )
    return (
      <PathDisplayRow>
        {wrappedContent}
        {staleIcon}
      </PathDisplayRow>
    )
  }
)
PathDisplay.displayName = 'PathDisplay'

interface ActualWorkRowItemProps {
  actualWork: ActualWork
  index: number
  taskType?: WbsItemTypeVO
  onTaskClick: (taskCode: string) => void
  showTimeFormat: boolean
  proxyUser?: UserEntity
}
const ActualWorkRowItem = memo(
  ({
    actualWork,
    index,
    taskType,
    onTaskClick,
    showTimeFormat,
    proxyUser,
  }: ActualWorkRowItemProps) => {
    const isNavigable = useMemo(
      () => !!actualWork.project,
      [actualWork.project]
    )

    const iconUrl = useMemo(
      () => actualWork.wbsItemType?.iconUrl ?? taskType?.iconUrl,
      [actualWork.wbsItemType?.iconUrl, taskType?.iconUrl]
    )

    const formattedEndDate = useMemo(
      () =>
        actualWork.endDate
          ? new DateVO(actualWork.endDate).format(DISPLAY_DATE_FORMAT_WITH_DAY)
          : null,
      [actualWork.endDate]
    )

    const handleClick = useCallback(
      (event: React.MouseEvent) => {
        const url = `${window.location.origin}/wbsItem/${actualWork.taskCode}`
        if (event.ctrlKey || event.metaKey) {
          window.open(url)
        } else if (isNavigable) {
          onTaskClick(actualWork.taskCode)
        }
      },
      [actualWork.taskCode, isNavigable, onTaskClick]
    )
    return (
      <>
        {index !== 0 && <Divider />}
        <ActualWorkRow>
          <TaskInfoRow>
            {actualWork.project && (
              <Tooltip
                title={actualWork.project.displayName}
                arrow={true}
                enterDelay={500}
                placement="bottom-start"
              >
                <ProjectInfoBox>
                  <ProjectAvatar
                    variant="circular"
                    src={actualWork.project.iconUrl}
                  />
                  <ProjectName>{actualWork.project.displayName}</ProjectName>
                </ProjectInfoBox>
              </Tooltip>
            )}
            <TaskStatus>
              <WbsItemStatusLabel status={actualWork.taskStatus} />
            </TaskStatus>
            {formattedEndDate && (
              <TaskEndDate>
                <TaskEndDateIcon />
                {formattedEndDate}
              </TaskEndDate>
            )}
            <DelayIconDiv>
              <DelayIcon
                wbsItem={{
                  status: actualWork.taskStatus,
                  scheduledDate: {
                    endDate: actualWork.endDate,
                  },
                }}
              />
            </DelayIconDiv>
          </TaskInfoRow>
          <TaskPathRow>
            <PathDisplay
              path={actualWork.path}
              pathStale={actualWork.pathStale}
            />
          </TaskPathRow>
          <TaskNameRow>
            <Tooltip
              title={actualWork.taskName}
              arrow={true}
              enterDelay={500}
              placement="bottom-start"
            >
              <ClickableArea isNavigable={isNavigable} onClick={handleClick}>
                {iconUrl && <TaskIcon src={iconUrl} />}
                <TaskName
                  sx={{
                    textDecoration: isNavigable ? 'underline' : 'none',
                  }}
                >
                  {actualWork.taskName}
                </TaskName>
              </ClickableArea>
            </Tooltip>
            <TaskActualHour>
              {showTimeFormat
                ? convertDecimalHourToHM(actualWork.hour)
                : Number(actualWork.hour).toFixed(2).toString()}
            </TaskActualHour>
            {proxyUser && (
              <Tooltip
                title={
                  <ProxyUserBox>
                    <Typography>{proxyUser.name}</Typography>
                    <Typography sx={{ marginLeft: '6px' }}>
                      {intl.formatMessage({
                        id: 'taskActualWork.proxy.registration.user.message',
                      })}
                    </Typography>
                  </ProxyUserBox>
                }
                placement="right"
                componentsProps={{
                  tooltip: {
                    sx: {
                      background: colorPalette.monotone[1],
                      boxShadow: '0px 1px 1px 1px #00000033',
                      width: 'fit-content',
                      '& .MuiTooltip-arrow': {
                        color: colorPalette.monotone[1],
                      },
                      display: 'flex',
                      justifyContent: 'center',
                      alignItems: 'center',
                    },
                  },
                }}
                arrow={true}
              >
                <ProxyUserAvatar variant="circular" src={proxyUser.iconUrl} />
              </Tooltip>
            )}
          </TaskNameRow>
        </ActualWorkRow>
      </>
    )
  }
)

ActualWorkRowItem.displayName = 'ActualWorkRowItem'
const TaskActualWorkTaskDialog = (props: Props) => {
  const {
    anchorEl,
    date,
    actualWorks,
    actualHour,
    event,
    onClose,
    openDetail,
  } = props
  const dispatch = useDispatch()
  const open = Boolean(anchorEl)
  const anchorOrigin: PopoverOrigin = {
    vertical: 'bottom',
    horizontal: 'right',
  }
  const transformOrigin: PopoverOrigin = {
    vertical: 'top',
    horizontal: 'left',
  }
  if (event.view && event.x > event.view.innerWidth / 2) {
    anchorOrigin.horizontal = 'left'
    transformOrigin.horizontal = 'right'
  }
  if (event.view && event.y > event.view.innerHeight / 2) {
    anchorOrigin.vertical = 'top'
    transformOrigin.vertical = 'bottom'
  }
  const taskType = props.wbsItemTypes?.task
  const { showTimeFormat, toggleTimeFormat } = useTimeFormatState()

  const handleTaskClick = useCallback(
    (taskCode: string) => {
      dispatch(
        pushFunctionLayer({
          externalId: APPLICATION_FUNCTION_EXTERNAL_ID.WBS_ITEM,
          code: taskCode,
          hideHeader: true,
        })
      )
    },
    [dispatch]
  )

  const totalTimeInHMFormat = useMemo(() => {
    let totalMinutes = 0

    actualWorks.forEach(work => {
      const hmFormat = convertDecimalHourToHM(work.hour)
      if (hmFormat) {
        const [hours, minutes] = hmFormat.split(':').map(Number)
        totalMinutes += hours * 60 + minutes
      }
    })

    const totalHours = Math.floor(totalMinutes / 60)
    const remainingMinutes = totalMinutes % 60
    return `${totalHours}:${remainingMinutes.toString().padStart(2, '0')}`
  }, [actualWorks])

  return (
    <MuiPopover
      open={open}
      anchorEl={anchorEl}
      anchorOrigin={anchorOrigin}
      transformOrigin={transformOrigin}
    >
      <ClickAwayListener onClickAway={onClose}>
        <DialogPaper>
          <HeaderContainer>
            <HeaderDate>{`${intl.formatMessage({
              id: 'workReport.dialog.header.name',
            })}  ${date.format('YYYY/MM/DD(ddd)')}`}</HeaderDate>
            <HeaderRightSection>
              <TimeFormatLabel>H:MM</TimeFormatLabel>
              <CompactSwitch
                checked={showTimeFormat}
                onChange={toggleTimeFormat}
              />
              {openDetail && (
                <Tooltip
                  title={props.intl.formatMessage({
                    id: 'dialog.view.tasks',
                  })}
                  placement="top"
                >
                  <OpenInNewIconButton onClick={openDetail}>
                    <OpenInNewRounded fontSize={'small'} />
                  </OpenInNewIconButton>
                </Tooltip>
              )}
            </HeaderRightSection>
          </HeaderContainer>
          <TaskListContainer>
            {actualWorks.map((actualWork, index) => (
              <ActualWorkRowItem
                key={actualWork.taskCode}
                actualWork={actualWork}
                index={index}
                taskType={taskType}
                onTaskClick={handleTaskClick}
                showTimeFormat={showTimeFormat}
                proxyUser={actualWork.proxyUser}
              />
            ))}
          </TaskListContainer>
          <LastDivider />
          <FooterContainer>
            <Total>
              Total:{' '}
              {showTimeFormat
                ? totalTimeInHMFormat
                : Number(actualHour).toFixed(2).toString()}
            </Total>
          </FooterContainer>
        </DialogPaper>
      </ClickAwayListener>
    </MuiPopover>
  )
}
// ============================================
// Styled Components
// ============================================

// Dialog Container
const DialogPaper = styled(Paper)({
  display: 'flex',
  flexDirection: 'column',
  maxHeight: '420px',
  width: '422px',
})

// Header Section
const HeaderContainer = styled(Box)({
  height: HEADER_HEIGHT,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  color: TextColor.WHITE,
  backgroundColor: '#888888',
  borderRadius: '5px 5px 0 0',
  flexShrink: 0,
})

const HeaderDate = styled(Typography)({
  marginLeft: '10px',
  fontSize: '12px',
})

const HeaderRightSection = styled(Box)({
  display: 'flex',
  alignItems: 'center',
  gap: '5px',
  marginRight: '10px',
})

const TimeFormatLabel = styled(Typography)({
  color: TextColor.WHITE,
  fontSize: '11px',
})

const CompactSwitch = styled(Switch)({
  width: 40,
  height: 18,
  padding: 0,
  '& .MuiSwitch-switchBase': {
    padding: 0,
    margin: '2px',
    transform: 'translateX(2px)',
    '&.Mui-checked': {
      color: colorPalette.monotone[0],
      transform: 'translateX(22px)',
      '& + .MuiSwitch-track': {
        backgroundColor: colorPalette.skyBlue[5],
      },
    },
  },
  '& .MuiSwitch-thumb': {
    width: 14,
    height: 14,
  },
  '& .MuiSwitch-track': {
    borderRadius: 9,
  },
})

const OpenInNewIconButton = styled(IconButton)({
  color: IconColor.WHITE,
  width: '18px',
  height: '18px',
  padding: '2px',
  margin: 0,
})

// List Section
const TaskListContainer = styled(List)({
  padding: `0 8px`,
  overflowX: 'hidden',
  overflowY: 'auto',
  flex: '1 1 auto',
  minHeight: 0,
  scrollbarGutter: 'stable',
}) as typeof List

const ActualWorkRow = styled(ListItem)({
  minHeight: '78px',
  padding: '5px 0',
  flexDirection: 'column',
  alignItems: 'flex-start',
})

const Divider = styled(MuiDivider)({
  borderTop: `1px dotted ${BorderColor.GREY}`,
  background: 'transparent',
})

// Task Item Section
const ProjectInfoBox = styled(Box)({
  display: 'flex',
  width: '190px',
  alignItems: 'center',
})

const ProjectAvatar = styled(Avatar)({
  width: 20,
  height: 20,
  marginRight: '5px',
  flexShrink: 0,
})

const ProjectName = styled(Typography)({
  overflow: 'hidden',
  whiteSpace: 'nowrap',
  textOverflow: 'ellipsis',
  fontWeight: FontWeight.BOLD,
  marginTop: '2px',
  paddingRight: '8px',
  width: '157px',
})

const TaskInfoRow = styled(Box)({
  display: 'flex',
  flexWrap: 'nowrap',
  alignItems: 'center',
  gap: '8px',
  minHeight: '28px',
})

const TaskStatus = styled('div')({
  display: 'inline-block',
  minWidth: '56px',
  marginTop: '-5px',
})

const TaskEndDate = styled(Typography)({
  display: 'flex',
  alignItems: 'center',
  fontSize: '11px',
  marginLeft: '-8px',
})

const TaskEndDateIcon = styled(AccessAlarmIcon)({
  fontSize: '13pt',
  marginTop: '-2px',
  marginRight: '2px',
})

const DelayIconDiv = styled('div')({
  display: 'flex',
  margin: '0px 3px',
})

const TaskPathRow = styled(Box)({
  display: 'flex',
  marginTop: '5px',
  marginLeft: '5px',
})

const PathDisplayRow = styled('div')({
  display: 'flex',
  alignItems: 'center',
  gap: '4px',
  minWidth: 0,
})

const TaskPath = styled(Typography)({
  fontSize: '11px',
  color: '#555',
  maxWidth: '360px',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
})

const TaskPathExpanded = styled(Typography)({
  fontSize: '11px',
  color: '#555',
  width: '360px',
  maxWidth: '360px',
  overflowX: 'auto',
  overflowY: 'hidden',
  whiteSpace: 'nowrap',
  '&::-webkit-scrollbar': { height: '8px' },
  '&::-webkit-scrollbar-track': {
    background: '#e0e0e0',
    borderRadius: '4px',
    marginTop: '2px',
  },
  '&::-webkit-scrollbar-thumb': {
    background: '#999',
    borderRadius: '4px',
    border: '1px solid #e0e0e0',
    '&:hover': { background: '#666' },
    '&:active': { background: '#444' },
  },
})

const TaskPathEllipsis = styled('span')({
  color: '#888',
  cursor: 'pointer',
  padding: '1px 3px',
  margin: '0 1px',
  borderRadius: '2px',
  fontSize: '11px',
  transition: 'all 0.2s ease',
  '&:hover': {
    color: colorPalette.blue[8],
    backgroundColor: colorPalette.blue[0],
  },
  '&:active': {
    transform: 'scale(0.95)',
  },
})

const TaskNameRow = styled(Box)({
  display: 'flex',
  alignItems: 'center',
  gap: '10px',
  marginLeft: '5px',
})

const ClickableArea = styled(Box)<{ isNavigable: boolean }>(
  ({ isNavigable }) => ({
    display: 'flex',
    alignItems: 'center',
    cursor: isNavigable ? 'pointer' : 'default',
    width: '300px',
    overflow: 'hidden',
    '&:hover': { opacity: isNavigable ? 0.7 : 1 },
  })
)

const TaskIcon = styled('img')({
  marginRight: '5px',
})

const TaskName = styled(Typography)({
  overflow: 'hidden',
  whiteSpace: 'nowrap',
  textOverflow: 'ellipsis',
  width: '320px',
  fontSize: '11px',
  fontWeight: FontWeight.BOLD,
})

const TaskActualHour = styled(Typography)({
  fontSize: '11px',
  fontWeight: 'bold',
  width: '50px',
  textAlign: 'right',
  whiteSpace: 'nowrap',
})

// Footer Section
const LastDivider = styled(MuiDivider)({
  borderTop: `1px solid ${BorderColor.GREY}`,
  background: 'transparent',
  margin: '0 10px',
})

const FooterContainer = styled(Box)({
  padding: '0 14px',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'flex-end',
  height: FOOTER_HEIGHT,
  flexShrink: 0,
})

const Total = styled(Typography)({
  fontWeight: FontWeight.BOLD,
})

const ProxyUserBox = styled(Box)({
  display: 'flex',
  flexDirection: 'row',
  alignItems: 'center',
  color: colorPalette.monotone[7],
  padding: '10px',
  fontWeight: FontWeight.BOLD,
  width: 'fit-content',
  whiteSpace: 'nowrap',
})

const ProxyUserAvatar = styled(Avatar)({
  marginRight: '5px',
  height: '23px',
  width: '23px',
  borderRadius: '50%',
})

const mapStateToProps = (state: AllState) => ({
  wbsItemTypes: state.project.wbsItemTypes,
})
export default connect(mapStateToProps)(injectIntl(TaskActualWorkTaskDialog))
